import React, { useState } from 'react';
import { Modal } from '../../CompositeComponents/Modal/Modal';
import { Button } from '../../BasicComponents/Button/Button';
import { Badge } from '../../BasicComponents/Badge/Badge';
import { formatMoney } from '../../../utils/currency';
import type { VariantAttribute, VariantMatrixProps, VariantOption } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function matchVariant(variants: VariantOption[], selection: Record<string, string>): VariantOption | undefined {
  return variants.find((v) =>
    Object.entries(selection).every(([k, val]) => v.attributes[k] === val),
  );
}

// ─── VariantMatrix ────────────────────────────────────────────────────────────

export function VariantMatrix({
  isOpen,
  onClose,
  productName,
  attributes,
  variants,
  onSelect,
  isLoading = false,
  currency = 'INR',
  parentStock = 0,
  className,
}: VariantMatrixProps) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  const [selection, setSelection] = useState<Record<string, string>>({});

  const selectedVariant = matchVariant(variants, selection);
  const isFullySelected = attributes.every((attr) => selection[attr.name] !== undefined);
  // Only worth explaining when nothing else can be picked: with a sellable
  // variant on screen the cashier has a way forward and needs no warning.
  const unallocatedParentStock =
    variants.some((v) => v.stock > 0) ? 0 : Number(parentStock) || 0;

  function handleAttrSelect(attrName: string, attrValue: string) {
    setSelection((prev) => ({ ...prev, [attrName]: attrValue }));
  }

  function isValueAvailable(attrName: string, attrValue: string): boolean {
    const partial = { ...selection, [attrName]: attrValue };
    return variants.some((v) =>
      Object.entries(partial).every(([k, val]) => v.attributes[k] === val) && v.stock > 0,
    );
  }

  function handleConfirm() {
    if (selectedVariant) {
      onSelect(selectedVariant);
      onClose();
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Select variant — ${productName}`}
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!isFullySelected || !selectedVariant || selectedVariant.stock === 0}
          >
            Add to Cart
            {selectedVariant && selectedVariant.stock > 0 && (
              <span className="ml-1 opacity-70">({fmt(selectedVariant.price)})</span>
            )}
          </Button>
        </div>
      }
    >
      <div className={`space-y-5 ${className ?? ''}`}>
        {isLoading ? (
          <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            Loading variants&hellip;
          </p>
        ) : variants.length === 0 ? (
          <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            No variants are available for this product.
          </p>
        ) : null}
        {!isLoading && attributes.map((attr: VariantAttribute) => (
          <div key={attr.name}>
            <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
              {attr.name}
              {selection[attr.name] && (
                <span className="ml-2 text-brand-600 dark:text-brand-400 font-normal">
                  {selection[attr.name]}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {attr.values.map((val) => {
                const available = isValueAvailable(attr.name, val);
                const active = selection[attr.name] === val;
                return (
                  <button
                    key={val}
                    type="button"
                    aria-label={`${attr.name}: ${val}${!available ? ' (out of stock)' : ''}`}
                    aria-disabled={!available}
                    aria-pressed={active}
                    onClick={() => available && handleAttrSelect(attr.name, val)}
                    className={[
                      'px-3 py-1.5 rounded-md border text-sm font-medium transition-colors',
                      active
                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                        : available
                        ? 'border-neutral-200 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:border-brand-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                        : 'border-neutral-100 dark:border-neutral-700 text-neutral-300 dark:text-neutral-600 cursor-not-allowed line-through',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Units sitting on the parent row are unallocated, not sellable: every
            sale must name a variant. Tell the cashier where they went rather
            than leaving a picker of dead options with no explanation. */}
        {!isLoading && unallocatedParentStock > 0 && (
          <div className="rounded-lg border border-warning-300 bg-warning-50 p-3 dark:border-warning-700 dark:bg-warning-900/20">
            <p className="text-sm font-semibold text-warning-900 dark:text-warning-100">
              No variant is in stock
            </p>
            <p className="mt-0.5 text-xs text-warning-800 dark:text-warning-200">
              {unallocatedParentStock} unit(s) are recorded against {productName} itself
              rather than a variant, so they cannot be sold yet. Assign them to a variant
              in Inventory first.
            </p>
          </div>
        )}

        {/* Selected variant info */}
        {isFullySelected && selectedVariant && (
          <div className={[
            'rounded-lg p-3 flex items-center justify-between',
            selectedVariant.stock === 0
              ? 'bg-danger-50 dark:bg-danger-900/20'
              : 'bg-neutral-50 dark:bg-neutral-800',
          ].join(' ')}>
            <div className="flex items-center gap-3">
              {/* Variant's own picture, or the main product image when it has
                  none — so every combination shows something recognisable. */}
              {selectedVariant.imageUrl ? (
                <img
                  src={selectedVariant.imageUrl}
                  alt={`${productName} ${Object.values(selectedVariant.attributes).join(' ')}`}
                  className="h-14 w-14 shrink-0 rounded-md object-cover"
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-neutral-200 text-neutral-400 dark:bg-neutral-700 dark:text-neutral-500"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16v12H4z" />
                  </svg>
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                  {fmt(selectedVariant.price)}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  SKU: {selectedVariant.sku}
                </p>
              </div>
            </div>
            {selectedVariant.stock === 0 ? (
              <Badge variant="outOfStock">Out of Stock</Badge>
            ) : (
              <Badge variant="active">{selectedVariant.stock} in stock</Badge>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
